import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const detectBrowserLanguage = () => {
  const lang = navigator.language || navigator.userLanguage || 'fr';
  return lang.startsWith('en') ? 'en' : 'fr';
};

const SUPPORTED = ['fr', 'en'];

const getInitialLanguage = () => {
  // 1. URL ?lang=
  try {
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang && SUPPORTED.includes(urlLang)) return urlLang;
  } catch (e) { /* ignore */ }
  // 2. localStorage
  const saved = localStorage.getItem('bpm_language');
  if (saved && SUPPORTED.includes(saved)) return saved;
  // 3. browser detection
  return detectBrowserLanguage();
};

const syncUrlLang = (language) => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', language);
    window.history.replaceState({}, '', url.toString());
  } catch (e) { /* ignore */ }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    localStorage.setItem('bpm_language', language);
    document.documentElement.lang = language;
    syncUrlLang(language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    isFrench: language === 'fr',
    isEnglish: language === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
